import React from "react";
import { Button, Tag, Tooltip } from "antd";
import { format, fromUnixTime } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faFilter } from "@fortawesome/free-solid-svg-icons";
import formatMoney from "../../../utils/formatMoney";
import OrderRewardCell from "./order-reward-cell";

/**
 * Cột bảng Đơn hàng admin.
 *
 * Bố cục: thông tin NV cần đối soát nhất (mã đơn, user, trạng thái, tiền) nằm ở đầu để
 * thấy ngay không phải cuộn ngang; thông tin phụ (sàn, TK Shopee, loại, Order status sàn,
 * phiên bản app, X2 cũ) gộp thành dòng chữ nhỏ dưới cột chính thay vì mỗi thứ một cột.
 * Trước đây 21 cột rộng ~2.300px, cột tiền bị đẩy ra ngoài màn laptop.
 */

export const STATUS_TAG = {
    1: { color: "orange", text: "Chờ duyệt" },
    2: { color: "green",  text: "Đã duyệt" },
    3: { color: "default",text: "Đã huỷ" },
    4: { color: "orange", text: "Chờ duyệt" },
};

// Màu tag trạng thái Shopee trong popup check
export const CHECK_STATUS_COLOR = {
    CONVERSION_STATUS_PENDING: "orange",
    CONVERSION_STATUS_COMPLETE: "green",
    CONVERSION_STATUS_CANCEL: "red",
};
// Rút gọn tên trạng thái Shopee để hiển thị badge cạnh nút Check (bỏ tiền tố CONVERSION_STATUS_)
const shortCheckStatus = (s) => (s ? s.replace(/^CONVERSION_STATUS_/, "") : null);

export const fmt = (v) => formatMoney.format(v * 1);
const fmtDate = (ts) => ts ? format(fromUnixTime(ts), "dd/MM/yy HH:mm") : "—";
/** Tên tài khoản shopee ngắn gọn từ cookiePlatform (vd: "shopee_dealngonboree" -> "dealngonboree") */
const shopAccount = (p) => p ? p.replace(/^(shopee|lazada|tiktok)_/, "") : null;

const Dash = () => <span className="text-content-3">—</span>;
/** Dòng chữ phụ dưới giá trị chính của ô. */
const Sub = ({ children, title }) => (
    <div className="text-[11px] leading-tight text-content-3" title={title}>{children}</div>
);

/**
 * @param {object} p
 * @param {(patch: object) => void} p.applySearch - lọc nhanh theo user
 * @param {object} p.checkedStatus - { [orderId]: "CONVERSION_STATUS_*" }
 * @param {Set} p.checkingIds - đơn đang check
 * @param {(id) => void} p.handleCheckOne
 * @param {(id) => void} p.confirmComplete
 */
export function buildOrderColumns({ applySearch, checkedStatus, checkingIds, handleCheckOne, confirmComplete }) {
    return [
        {
            title: "Order ID", dataIndex: "id", key: "id", width: 160, fixed: "left",
            render: (v, { campId, cookiePlatform }) => (
                <div className="leading-tight">
                    <div className="font-medium">{v}</div>
                    <Sub title={cookiePlatform || undefined}>
                        {campId}{cookiePlatform ? ` · ${shopAccount(cookiePlatform)}` : ""}
                    </Sub>
                </div>
            ),
        },
        {
            title: "User", dataIndex: "userId", key: "userId", width: 100,
            render: (v, { refId }) => (
                <div className="leading-tight">
                    {v ? (
                        <Tooltip title="Lọc theo user này">
                            <button
                                onClick={() => applySearch({ userId: String(v) })}
                                className="text-left hover:text-blue-600 transition-colors flex items-center gap-1 group"
                            >
                                <span>{v}</span>
                                <FontAwesomeIcon icon={faFilter} className="text-xs text-content-3 group-hover:text-blue-500" />
                            </button>
                        </Tooltip>
                    ) : <Dash />}
                    {/* "Ref" = người mua có người giới thiệu (đơn có HH ref) */}
                    <Sub>{refId ? <span className="text-amber-600">Ref</span> : "User"}</Sub>
                </div>
            ),
        },
        {
            title: "Trạng thái", dataIndex: "status", key: "status", width: 120,
            render: (s, { orderStatus }) => {
                const t = STATUS_TAG[s];
                return (
                    <div className="leading-tight">
                        {t ? <Tag color={t.color} className="m-0">{t.text}</Tag> : s}
                        {orderStatus && <Sub title="Order status phía sàn">{orderStatus}</Sub>}
                    </div>
                );
            },
        },
        { title: "Hoa hồng", dataIndex: "commission",     key: "commission",     align: "right", width: 100, render: fmt },
        { title: "HH user",  dataIndex: "userCommission", key: "userCommission", align: "right", width: 100, render: fmt },
        {
            title: "HH ref", dataIndex: "refCommission", key: "refCommission", align: "right", width: 110,
            // Ref nhận 2 khoản: HH ref của đơn (cột này) + ref của tiền thưởng (sổ RewardOrders).
            // Hiện cả 2 để khỏi đối chiếu nhầm với cột "Ref của thưởng" ở trang đợt thưởng.
            render: (v, { refId, reward }) => {
                if (!refId) return <Dash />;
                const refReward = reward?.refAmount || 0;
                if (!refReward) return <span className="text-amber-600">{fmt(v)}</span>;
                const paidNote = reward.refPaidAmount >= refReward ? "đã cộng ví" : "chưa cộng ví — chờ tiền hoàn đơn vào ví";
                return (
                    <Tooltip title={`HH ref đơn ${fmt(v)} + ref thưởng ${fmt(refReward)} (${paidNote}) = ${fmt(Number(v || 0) + refReward)}`}>
                        <div className="flex flex-col items-end leading-tight">
                            <span className="text-amber-600">{fmt(v)}</span>
                            <span className="text-[11px] text-green-600">+{fmt(refReward)} thưởng</span>
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: "Ref ID", dataIndex: "refId", key: "refId", width: 75,
            render: (v) => v ?? <Dash />,
        },
        {
            // Đợt thưởng MỚI (RewardOrders); tag X2 = hệ số đợt CŨ (appMultiplierApplied) — 2 cơ chế khác nhau.
            title: "Thưởng", dataIndex: "reward", key: "reward", width: 130,
            render: (v, { x2, x2Multiplier }) => (
                <div className="flex flex-col items-start gap-0.5">
                    <OrderRewardCell reward={v} />
                    {x2 && (
                        <Tooltip title="Hệ số X2 của chiến dịch cũ">
                            <Tag color="red" className="m-0">X2 cũ · x{x2Multiplier || 2}</Tag>
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: "Kênh", key: "channel", width: 100,
            render: (_, { channel, appVersion, os }) => (
                <div className="leading-tight">
                    {channel === "app" ? <Tag color="purple" className="m-0">App</Tag>
                        : channel === "web" ? <Tag color="cyan" className="m-0">Web</Tag>
                            : <Dash />}
                    {appVersion && <Sub>{appVersion}{os ? ` · ${os}` : ""}</Sub>}
                </div>
            ),
        },
        {
            // Mốc quyết định thưởng/đối soát — để trước cột Click cho NV nhìn thấy đầu tiên.
            title: <Tooltip title="Giờ sàn ghi nhận đơn — dùng để xét điều kiện đợt thưởng và thứ tự chia ngân sách">Đặt hàng</Tooltip>,
            dataIndex: "purchaseTime", key: "purchaseTime", width: 125, render: fmtDate,
        },
        {
            title: <Tooltip title="Giờ sàn báo đơn hoàn thành (chưa hoàn thành thì để trống)">Hoàn thành</Tooltip>,
            dataIndex: "completeTime", key: "completeTime", width: 125, render: fmtDate,
        },
        {
            // Đổi tên từ "Ngày tạo" → "Click": đây là giờ user bấm link, thường lệch vài giờ so
            // với giờ đặt hàng nên NV hay đối chiếu nhầm với màn đơn hàng của user.
            title: <Tooltip title="Giờ user bấm link (clickTime) — không phải giờ đặt hàng">Click</Tooltip>,
            dataIndex: "clickTime", key: "clickTime", width: 125, render: fmtDate,
        },
        {
            title: <Tooltip title="Giờ tiền hoàn được cộng vào ví user">Ngày TT</Tooltip>,
            dataIndex: "payTime", key: "payTime", width: 125,
            render: (v, { payedToUser }) => (
                <span className="flex items-center gap-1">
                    {payedToUser && <FontAwesomeIcon className="text-green-600" icon={faCheckCircle} />}
                    {fmtDate(v)}
                </span>
            ),
        },
        {
            title: "", key: "action", width: 140, fixed: "right",
            render: (_, { id, status, campId }) => (
                <div className="flex gap-1 justify-end items-center">
                    {checkedStatus[id] && (
                        <Tooltip title={`Trạng thái Shopee thật lúc check: ${checkedStatus[id]}`}>
                            <Tag color={CHECK_STATUS_COLOR[checkedStatus[id]] || "default"} className="m-0">
                                {shortCheckStatus(checkedStatus[id])}
                            </Tag>
                        </Tooltip>
                    )}
                    {(campId || "shopee") === "shopee" && (
                        <Button size="small" loading={checkingIds.has(id)} onClick={() => handleCheckOne(id)}>
                            Check
                        </Button>
                    )}
                    {status === 2 || status === 3 ? null : (
                        <Button size="small" type="primary" ghost onClick={() => confirmComplete(id)}>
                            Duyệt
                        </Button>
                    )}
                </div>
            ),
        },
    ];
}
